import React, { useRef, useState, useMemo } from 'react';
import { RadarChartProps, RadarChartDimension } from '../../types/assessment';
import { DEFAULT_RADAR_COLORS, getAssessmentLevel } from '../../utils/assessmentUtils';

const RadarChart: React.FC<RadarChartProps> = ({
  title,
  data,
  dimensions,
  maxScore = 10,
  size = 'medium',
  showValues = true,
  showLegend = true,
  interactive = true,
  colors = DEFAULT_RADAR_COLORS,
  onDimensionHover,
  onDimensionClick,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredDimension, setHoveredDimension] = useState<string | null>(null);
  const [selectedDimension, setSelectedDimension] = useState<string | null>(null);

  // Chart dimensions based on size
  const chartSizes = {
    small: { width: 280, height: 280, radius: 100 },
    medium: { width: 360, height: 360, radius: 130 },
    large: { width: 480, height: 480, radius: 180 }
  };

  const { width, height, radius } = chartSizes[size];
  const center = useMemo(() => ({ x: width / 2, y: height / 2 }), [width, height]);

  // Calculate points for radar chart
  const calculatePoints = useMemo(() => {
    const angleStep = (2 * Math.PI) / dimensions.length;
    
    return dimensions.map((dimension, index) => {
      const angle = index * angleStep - Math.PI / 2; // Start from top
      const score = data[dimension.key] || 0;
      const normalizedScore = Math.min(score, maxScore) / maxScore;
      
      return {
        dimension,
        angle,
        score,
        normalizedScore,
        x: center.x + Math.cos(angle) * radius * normalizedScore,
        y: center.y + Math.sin(angle) * radius * normalizedScore,
        labelX: center.x + Math.cos(angle) * (radius + 30),
        labelY: center.y + Math.sin(angle) * (radius + 30),
        axisTipX: center.x + Math.cos(angle) * radius,
        axisTipY: center.y + Math.sin(angle) * radius,
      };
    });
  }, [data, dimensions, maxScore, radius, center]);

  // Generate grid circles
  const gridLevels = [2, 4, 6, 8, 10];
  const gridCircles = gridLevels.map(level => ({
    level,
    radius: (level / maxScore) * radius,
    color: level === 6 ? colors.fair : 
           level === 8 ? colors.good : colors.grid
  }));

  // Generate radar area path
  const radarPath = useMemo(() => {
    if (calculatePoints.length === 0) return '';
    
    const pathData = calculatePoints.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ') + ' Z';
    
    return pathData;
  }, [calculatePoints]);

  // Handle dimension interactions
  const handleDimensionHover = (dimension: RadarChartDimension | null) => {
    if (!interactive) return;
    
    setHoveredDimension(dimension?.key || null);
    onDimensionHover?.(dimension);
  };

  const handleDimensionClick = (dimension: RadarChartDimension) => {
    if (!interactive) return;
    
    setSelectedDimension(dimension.key);
    onDimensionClick?.(dimension);
  };

  // Get color for score
  const getScoreColor = (score: number) => {
    const level = getAssessmentLevel(score);
    return level.color;
  };

  return (
    <div className="radar-chart-container" style={{ textAlign: 'center' }}>
      {/* Title */}
      <h3 style={{ 
        marginBottom: '16px', 
        fontSize: size === 'large' ? '20px' : '16px',
        color: colors.text,
        fontWeight: 'bold'
      }}>
        {title}
      </h3>

      {/* SVG Chart */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{ background: 'transparent' }}
        >
          {/* Grid circles */}
          {gridCircles.map(circle => (
            <circle
              key={circle.level}
              cx={center.x}
              cy={center.y}
              r={circle.radius}
              fill="none"
              stroke={circle.color}
              strokeWidth={circle.level === 6 || circle.level === 8 ? "2" : "1"}
              strokeDasharray={circle.level === 6 || circle.level === 8 ? "5,5" : "none"}
              opacity={circle.level === 6 || circle.level === 8 ? "0.8" : "0.3"}
            />
          ))}

          {/* Grid labels */}
          {gridLevels.map(level => (
            <text
              key={level}
              x={center.x + 5}
              y={center.y - (level / maxScore) * radius + 4}
              fontSize="10"
              fill={colors.text}
              opacity="0.6"
            >
              {level}
            </text>
          ))}

          {/* Axis lines */}
          {calculatePoints.map((point, index) => (
            <line
              key={index}
              x1={center.x}
              y1={center.y}
              x2={point.axisTipX}
              y2={point.axisTipY}
              stroke={colors.grid}
              strokeWidth="1"
              opacity="0.3"
            />
          ))}

          {/* Radar area */}
          <path
            d={radarPath}
            fill={colors.good}
            fillOpacity="0.2"
            stroke={colors.good}
            strokeWidth="2"
          />

          {/* Data points */}
          {calculatePoints.map((point, index) => {
            const isHovered = hoveredDimension === point.dimension.key;
            const isSelected = selectedDimension === point.dimension.key;
            
            return (
              <g key={index}>
                {/* Point circle */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isHovered || isSelected ? "6" : "4"}
                  fill={getScoreColor(point.score)}
                  stroke="white"
                  strokeWidth="2"
                  style={{ 
                    cursor: interactive ? 'pointer' : 'default',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={() => handleDimensionHover(point.dimension)}
                  onMouseLeave={() => handleDimensionHover(null)}
                  onClick={() => handleDimensionClick(point.dimension)}
                />

                {/* Score value */}
                {showValues && (
                  <text
                    x={point.x}
                    y={point.y - 12}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="bold"
                    fill={getScoreColor(point.score)}
                  >
                    {point.score.toFixed(1)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Dimension labels */}
          {calculatePoints.map((point, index) => {
            const isHovered = hoveredDimension === point.dimension.key;
            
            return (
              <text
                key={index}
                x={point.labelX}
                y={point.labelY}
                textAnchor={point.labelX > center.x ? 'start' : point.labelX < center.x ? 'end' : 'middle'}
                dominantBaseline={point.labelY > center.y ? 'hanging' : point.labelY < center.y ? 'alphabetic' : 'middle'}
                fontSize={size === 'large' ? "12" : "10"}
                fontWeight={isHovered ? "bold" : "normal"}
                fill={isHovered ? colors.good : colors.text}
                style={{ 
                  cursor: interactive ? 'pointer' : 'default',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={() => handleDimensionHover(point.dimension)}
                onMouseLeave={() => handleDimensionHover(null)}
                onClick={() => handleDimensionClick(point.dimension)}
              >
                {point.dimension.label}
              </text>
            );
          })}
        </svg>

        {/* Tooltip for hovered dimension */}
        {hoveredDimension && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            right: '10px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            lineHeight: '1.4',
            zIndex: 1000,
            pointerEvents: 'none'
          }}>
            {dimensions.find(d => d.key === hoveredDimension)?.description}
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div style={{ 
          marginTop: '20px', 
          display: 'flex', 
          justifyContent: 'center', 
          flexWrap: 'wrap',
          gap: '16px',
          padding: '0 20px'
        }}>
          {[
            { label: 'Excellent (9-10)', color: colors.excellent },
            { label: 'Good (7-8)', color: colors.good },
            { label: 'Fair (4-6)', color: colors.fair },
            { label: 'Needs Improvement (1-3)', color: colors.poor }
          ].map(item => (
            <div key={item.label} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '12px',
              color: colors.text,
              fontWeight: '500'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: item.color,
                borderRadius: '50%',
                marginRight: '6px'
              }} />
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RadarChart;